import { useEffect, useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { renderAmount as renderAmountOf } from '../utils/formatPrice';

// Espera que o utilizador pare de escrever antes de pedir os totais à API.
const DEBOUNCE_MS = 300;

/**
 * Totais de uma encomenda calculados pela API a partir das linhas dadas.
 * As linhas são `{ product_id, quantity }` — cada página mapeia as suas.
 * Respostas fora de ordem são descartadas: só a última alteração manda.
 */
const useOrderTotals = (items) => {
  const [orderTotals, setOrderTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const payload = JSON.stringify(items || []);

  useEffect(() => {
    const lines = JSON.parse(payload);

    if (lines.length === 0) {
      setOrderTotals(null);
      setError('');
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const timer = setTimeout(async () => {
      try {
        const response = await axiosInstance.post('/order/calculate-totals', { items: lines });
        if (cancelled) return;
        setOrderTotals(response);
        setError('');
      } catch (err) {
        if (cancelled) return;
        setOrderTotals(null);
        // Um 404 é uma linha cujo produto já não existe: atualizar a página não
        // resolve, porque o carrinho vive no localStorage. Só removê-la resolve.
        setError(
          err.status === 404
            ? 'Há artigos que já não estão disponíveis. Remove-os do carrinho, ou esvazia-o, para continuares.'
            : 'Não foi possível calcular os totais. Por favor, atualiza a página.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [payload]);

  const renderAmount = (amount) => renderAmountOf(amount, { loading, totals: orderTotals });

  return { orderTotals, loading, error, renderAmount };
};

export default useOrderTotals;
